import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import React, {useMemo, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {InstanceCloneEntityTypeEnum} from 'src/api/ProactApiClient';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {Textbox} from 'src/design/atoms/Textbox';
import type {IStepItem} from 'src/design/molecules/VerticalLinearStepper';
import {VerticalLinearStepper} from 'src/design/molecules/VerticalLinearStepper';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {SidebarLinkInstances} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useCloneInstanceMutation,
  useGetInstanceQuery,
} from 'src/redux/queries/instance';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {mapEnumToSelectOption} from 'src/utilities/enumUtils';
import {instanceDocumentTitle} from '../instanceHelpers';
import {CloneInstanceConfirmation} from './CloneInstanceConfirmation';

const items = mapEnumToSelectOption(InstanceCloneEntityTypeEnum);

interface CloneInstanceFormValues {
  newInstanceName: string;
}

export const CloneInstancePage: React.FC = () => {
  useSetDocumentTitle(instanceDocumentTitle);
  const {setAlert} = useAlert();

  const {id} = useParams();

  const {control} = useForm<CloneInstanceFormValues>({
    defaultValues: {newInstanceName: ''},
  });
  const newInstanceName = useWatch({control, name: 'newInstanceName'});

  const [selectedEntities, setSelectedEntities] = useState<
    InstanceCloneEntityTypeEnum[]
  >([]);

  const [isSubmitted, setIsSubmitted] = useState<string | undefined>();

  const {data: item} = useGetInstanceQuery(Number(id), {
    skip: id === undefined,
  });

  const [cloneInstance] = useCloneInstanceMutation();

  function handleEntityChange(
    entityId: InstanceCloneEntityTypeEnum,
    checked: boolean
  ): void {
    if (checked === false) {
      setSelectedEntities(selectedEntities.filter(i => i !== entityId));
    } else {
      setSelectedEntities([...selectedEntities, entityId]);
    }
  }

  const handleCompletedSteps = async () => {
    try {
      const jobId = await cloneInstance({
        targetInstanceId: Number(id),
        newInstanceName: newInstanceName,
        entitiesToClone: selectedEntities,
      }).unwrap();

      setIsSubmitted(jobId ?? '');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const steps: IStepItem[] = [
    {
      label: 'New Instance Name',
      handleIsContinueDisabled: () => newInstanceName === '',
      component: (
        <Box mb="lg">
          <p>
            You have initiated the instance cloning process for the{' '}
            <b>{item?.name}</b> instance.
          </p>
          <p>
            This process will guide you through selecting what items you want to
            clone from this instance and create a new instance with copies of
            those items.
          </p>
          <p>
            To continue, please provide the name of the new instance you wish to
            create.
          </p>
          <Controller
            name="newInstanceName"
            control={control}
            render={({field}) => (
              <Textbox
                label="New Instance Name"
                value={field.value}
                onChangeCallback={field.onChange}
                onBlurCallback={field.onBlur}
              />
            )}
          />
        </Box>
      ),
    },
    {
      label: 'Select Items to Clone',
      handleIsContinueDisabled: () => selectedEntities.length === 0,
      component: (
        <Box>
          <p>
            You have initiated the instance cloning process for the{' '}
            <b>{item?.name}</b> instance. It will be cloned to{' '}
            <b>{newInstanceName}</b>.
          </p>
          <p>
            You may choose to clone any of the items below from the{' '}
            <b>{item?.name}</b> instance. Enable the checkbox next to each item
            you wish to clone.
          </p>
          <Alert color="yellow" title="Sensitive data is not cloned">
            <ul>
              <li>
                Submissions, personnel data, or other instance-specific data
                which may be sensitive is not cloned.
              </li>
              <li>
                For example, if you clone "Tasks", any reference to personnel
                data is removed.
              </li>
              <li>
                Or, if you clone "Form Definitions", any form submissions for
                those definitions in the previous instance is not cloned.
              </li>
              <li>
                You may need to perform updates to the cloned items to ensure
                they function as expected.
              </li>
            </ul>
          </Alert>
          <Box>
            <p>
              <b>Items to Clone:</b>
            </p>
            {items.map(option => {
              return (
                <CheckboxRow
                  key={option.id}
                  checked={selectedEntities.includes(
                    option.id as InstanceCloneEntityTypeEnum
                  )}
                  label={option.label}
                  onChange={(_, checked) =>
                    handleEntityChange(
                      option.id as InstanceCloneEntityTypeEnum,
                      checked
                    )
                  }
                />
              );
            })}
          </Box>
        </Box>
      ),
    },
    {
      label: 'Confirm Details',
      component: (
        <Box>
          <p>
            You have initiated the instance cloning process for the{' '}
            <b>{item?.name}</b> instance. It will be cloned to{' '}
            <b>{newInstanceName}</b>.
          </p>
          <p>
            The items below will be cloned from the <b>{item?.name}</b> instance
            into the new instance, <b>{newInstanceName}</b>.
          </p>
          <p>
            Are you sure you wish to continue? You can use the 'Back' button
            below to edit these details if needed.
          </p>

          <p>
            <b>Items to Clone:</b>
          </p>
          <ul>
            {items.map(item => {
              return <li key={item.id}>{item.label}</li>;
            })}
          </ul>
        </Box>
      ),
    },
  ];

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const crumbs: BreadcrumbItem[] = [
      {label: 'Instances', to: PathUtility.Instances()},
    ];
    if (item?.name) {
      crumbs.push({
        label: item.name,
        to: PathUtility.InstancesView(String(id), 'overview'),
      });
    }
    crumbs.push({label: 'Clone'});
    return crumbs;
  }, [id, item?.name]);

  const fallbackTo = PathUtility.InstancesView(String(id), 'overview');

  return (
    <ContentWrapper>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInstances.icon}>
          {item ? `Clone ${item?.name}` : 'Clone Instance'}
        </PageHeader.Title>
        {item?.internalIdentifier && (
          <PageHeader.Meta>{item.internalIdentifier}</PageHeader.Meta>
        )}
      </PageHeader>
      <Box mt="md">
        {isSubmitted === undefined && (
          <VerticalLinearStepper
            steps={steps}
            onCompletedSteps={handleCompletedSteps}
          />
        )}
        {isSubmitted === '' && (
          <Box>
            Your request was submitting, but an issue occured when returning the
            request status. The cloning operation may still complete. Please
            allow some time to verify if the request completed before submitting
            another request.
          </Box>
        )}
        {isSubmitted !== undefined && isSubmitted !== '' && (
          <CloneInstanceConfirmation id={isSubmitted} />
        )}
      </Box>
    </ContentWrapper>
  );
};

export default CloneInstancePage;

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.InstancesClone(':id'),
  element: <CloneInstancePage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
